import { PaymentModuleId } from '@foundation/apis/payments';
import { SupportedLanguage } from '@foundation/localization';
import Clarity from '@microsoft/clarity';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import { useEffect, useState, JSX } from 'react';

import { LoadingSpinner, LoadingSpinnerSize } from '@/components/common';
import { AlertWarning } from '@/components/common/alerts';
import { RedirectStatus } from '@/components/contexts/params';
import { PayButton } from '@/components/pages/stored-instruments/utils/PayButton';
import { useUserFriendlyPaymentModuleName } from '@/hooks/useUserFriendlyPaymentModuleName';
import Logger, { LoggerOrigin } from '@/services/Logger';
import TelemetryService from '@/services/telemetry/TelemetryService';
import YookassaService from '@/services/yookassa/YookassaService';
import { redirect } from '@/utils/redirect';

interface IPayWithYookassaBtnProps {
  navigation?: JSX.Element | null;
}

export function PayWithYookassaBtn({ navigation }: IPayWithYookassaBtnProps) {
  const { accessToken } = useAuth();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const paymentModuleT = useUserFriendlyPaymentModuleName();

  const [redirectUrl, setRedirectUrl] = useState<string>('');
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    setLoading(true);
    setError(undefined);

    YookassaService.getRedirectUrl(
      language as SupportedLanguage,
      accessToken ?? '',
    )
      .then(setRedirectUrl)
      .catch((e: Error) => {
        TelemetryService.trackException(e, 'PayWithYookassaBtn');
        Logger.error(
          LoggerOrigin.ReactComponent,
          'Yookassa URL fetch failure',
          e,
        );
        setError(e);
        Clarity.setTag('pay', [
          RedirectStatus.Failure,
          paymentModuleT(PaymentModuleId.Yookassa),
        ]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [accessToken, language, paymentModuleT]);

  if (error) {
    return (
      <AlertWarning
        title={t('Something went wrong!')}
        description={t(
          'OnlinePaymentMethod payments are currently unavailable.',
        )}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="h-14">
        <LoadingSpinner size={LoadingSpinnerSize.Small} />
      </div>
    );
  }

  return redirectUrl !== '' ? (
    <div className="flex space-x-2">
      {navigation}
      <PayButton
        onClick={() => {
          redirect(redirectUrl);
        }}
      />
    </div>
  ) : null;
}
